"use client";
import React, { FC, useMemo } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { Store } from "~/bff/types/Store";
import { StoreDetailsPageTranslations } from "~/bff/types/StoreDetailsPageTranslations";
import { StoreService as StoreServiceType } from "~/bff/types/StoreService";
import { CombinedLink } from "~/components/combined-link/component";
import { ProductImage } from "~/components/product-image/component";
import { Proportions } from "~/components/product-image/types";
import { getStoreOrServiceLabel } from "~/components/store-details-page/utils/get-store-or-service-label";
import { getLinkUrl } from "~/helpers/locales";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Nullable } from "~/types/general.types";

import { STORE_SERVICE_CLASSES } from "./constants";
import { StoreServiceContainer } from "./styled";

export interface StoreServiceProps {
  store: Store;
  service: StoreServiceType;
  translations: Nullable<StoreDetailsPageTranslations> | undefined;
  className?: string;
}

export const StoreService: FC<StoreServiceProps> = ({
  store,
  service,
  translations,
  className,
}) => {
  const { locale } = useParams<GlobalPathParams>();

  const openNowLabel = useMemo(() => {
    return getStoreOrServiceLabel({
      regularHours: service.hours,
      holidayHours: service.holidayHours,
      timezone: store.timezone ?? "",
      translations,
    });
  }, [service.holidayHours, service.hours, store.timezone, translations]);

  const link = service.url && getLinkUrl(locale, service.url);
  const bookingLink =
    service.bookingCTA?.link && getLinkUrl(locale, service.bookingCTA.link);

  return (
    <StoreServiceContainer
      className={className}
      {...getTestAutomationProps("store-service")}
    >
      {link && (
        <CombinedLink
          underline="none"
          href={link}
          className={STORE_SERVICE_CLASSES.link}
          aria-label={service.name ?? ""}
        >
          <span className={STORE_SERVICE_CLASSES.linkText}>{service.name}</span>
        </CombinedLink>
      )}
      <ProductImage
        className={STORE_SERVICE_CLASSES.serviceImage}
        proportions={Proportions.p1to1}
        src={service?.image?.url ?? ""}
      />
      <Box className={STORE_SERVICE_CLASSES.serviceInfoBlock}>
        <Typography className={STORE_SERVICE_CLASSES.title}>
          {service.name}
        </Typography>
        {service.hours && (
          <Typography
            className={STORE_SERVICE_CLASSES.subtitle}
            {...getTestAutomationProps("service-hours")}
          >
            {openNowLabel}
          </Typography>
        )}
        {bookingLink && (
          <CombinedLink
            underline="none"
            href={bookingLink}
            className={STORE_SERVICE_CLASSES.serviceDetailLink}
            aria-label={translations?.viewDetails ?? ""}
            {...getTestAutomationProps("service-view-details")}
          >
            <Typography component="span" variant="body2">
              {translations?.viewDetails}
            </Typography>
          </CombinedLink>
        )}
      </Box>
    </StoreServiceContainer>
  );
};
